import { Link } from 'react-router-dom';
import { BookOpen, Building2, ChevronLeft, Compass, Eye, Target } from 'lucide-react';
import AboutProseSection from '../../components/public/AboutProseSection.jsx';
import BrandTextHighlight from '../../components/public/BrandTextHighlight.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import SectionAmbientGradient from '../../components/public/SectionAmbientGradient.jsx';
import aboutUs from '../../content/aboutUs.json';
import { BRAND_LEGAL_NAME } from '../../constants/brand.js';

const ICONS = {
  about: Building2,
  objective: Target,
  history: BookOpen,
  mission: Compass,
  vision: Eye,
};

export default function AboutPage() {
  const { hero, about, objective, history, mission, vision } = aboutUs;

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-white/10">
        <SectionAmbientGradient />
        <div className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Volver al inicio
          </Link>
          <ScrollReveal className="mt-10 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">{hero.eyebrow}</p>
            <h1 className="mt-4 font-display text-4xl font-bold text-white md:text-5xl">{hero.title}</h1>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-accent-lime">
              {hero.legalName || BRAND_LEGAL_NAME}
            </p>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">{hero.subtitle}</p>
          </ScrollReveal>
        </div>
      </section>

      <AboutProseSection
        title={about.title}
        paragraphs={about.paragraphs}
        icon={ICONS.about}
        variant="navy"
        className="bg-white"
      />

      <AboutProseSection
        title={objective.title}
        paragraphs={objective.paragraphs}
        icon={ICONS.objective}
        variant="green"
        className="bg-slate-50"
      />

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <ScrollReveal className="max-w-3xl">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-navy/80 ring-1 ring-white/10">
                <ICONS.history className="h-7 w-7 text-accent-lime" strokeWidth={1.75} aria-hidden />
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold text-white md:text-3xl">{history.title}</h2>
                <p className="mt-2 text-slate-400">
                  <BrandTextHighlight text={history.intro} />
                </p>
              </div>
            </div>
          </ScrollReveal>

          <ol className="relative mt-14 space-y-0 border-l border-[#1acb12]/35 pl-8 md:pl-10 lg:ml-4">
            {history.milestones.map((m, i) => (
              <ScrollReveal key={m.title} delayMs={i * 80} className="relative pb-12 last:pb-0">
                <span
                  className="absolute -left-[2.125rem] top-1 flex h-4 w-4 rounded-full bg-[#1acb12] ring-4 ring-[#030712] md:-left-[2.375rem]"
                  aria-hidden
                />
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-lime">{m.year}</p>
                <h3 className="mt-2 font-display text-xl font-semibold text-white">{m.title}</h3>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-base">
                  <BrandTextHighlight text={m.text} />
                </p>
              </ScrollReveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Identidad institucional</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-accent-blue md:text-3xl">Misión y visión</h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <ScrollReveal delayMs={0}>
              <PremiumCard hover variant="light" className="h-full">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0d2b7f]/10">
                  <ICONS.mission className="h-6 w-6 text-[#0d2b7f]" strokeWidth={1.75} aria-hidden />
                </span>
                <h3 className="mt-6 font-display text-xl font-semibold text-slate-800">{mission.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{mission.text}</p>
              </PremiumCard>
            </ScrollReveal>

            <ScrollReveal delayMs={75}>
              <PremiumCard hover variant="light" className="h-full">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1acb12]/12">
                  <ICONS.vision className="h-6 w-6 text-[#00A63E]" strokeWidth={1.75} aria-hidden />
                </span>
                <h3 className="mt-6 font-display text-xl font-semibold text-slate-800">{vision.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">{vision.text}</p>
              </PremiumCard>
            </ScrollReveal>
          </div>

          <ScrollReveal delayMs={120} className="mt-12 text-center">
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center rounded-lg border border-[#1acb12]/40 bg-brand-green px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-green-dark"
            >
              Contáctanos
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
